import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Loader } from '../components/ui/Loader';
import { SmsChecker } from '../components/temporary-numbers/SmsChecker';
import { useGetPermanentNumberQuery, useRenewPermanentNumberMutation, useLazyGetRenewalPriceQuery, useRefundPermanentNumberMutation } from '../store/api/permanentNumbersApi';
import { formatDate } from '../utils/dateFormatter';
import { useLanguage } from '../hooks/useLanguage';

export function PermanentNumberDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const { data, isLoading, error, refetch } = useGetPermanentNumberQuery(id || '');

  const number = data?.data.permanentNumber;

  const isCodeReceived = useMemo(() => {
    if (!number) return false;
    return Boolean(number.isCodeReceived || (number.lastReceivedCode && number.lastReceivedCode !== "not received") || number.receivedCodes?.length);
  }, [number]);

  const isTextVerified = number?.serviceProvider === 'textverified';

  const canRenew = useMemo(() => {
    if (!number?.expiresAt) return false;
    if (isTextVerified && !isCodeReceived) return false;
    return new Date(number.expiresAt) > new Date();
  }, [number, isCodeReceived, isTextVerified]);

  const [refundWindowExpired, setRefundWindowExpired] = useState(false);

  // Set a timer to expire the refund window after exactly 5 minutes from purchase
  useEffect(() => {
    if (!number?.purchasedAt) return;
    setRefundWindowExpired(false);
    const purchasedTime = new Date(number.purchasedAt).getTime();
    const remaining = (5 * 60 * 1000) - (Date.now() - purchasedTime);
    if (remaining <= 0) {
      setRefundWindowExpired(true);
      return;
    }
    const timer = setTimeout(() => setRefundWindowExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [number?.purchasedAt]);

  const canRefund = !number?.isCodeReceived && number?.purchasedAt && !refundWindowExpired;

  // Track if code arrived while this component was already open (vs loaded fresh after code received)
  const initialIsCodeReceived = useRef<boolean | null>(null);
  const [codeArrivedWhileOpen, setCodeArrivedWhileOpen] = useState(false);
  useEffect(() => {
    if (!number) return;
    if (initialIsCodeReceived.current === null) {
      initialIsCodeReceived.current = isCodeReceived;
      return;
    }
    if (initialIsCodeReceived.current === false && isCodeReceived) {
      setCodeArrivedWhileOpen(true);
    }
  }, [isCodeReceived, number]);

  const refundBlockedByCode = codeArrivedWhileOpen && number?.purchasedAt && !refundWindowExpired;

  // Poll every 5s while refund is available to detect code arrival in real-time
  useEffect(() => {
    if (!canRefund) return;
    const interval = setInterval(() => { refetch(); }, 5000);
    return () => clearInterval(interval);
  }, [canRefund, refetch]);

  const [showRenewPicker, setShowRenewPicker] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [renewPermanentNumber, { isLoading: isRenewing }] = useRenewPermanentNumberMutation();
  const [fetchRenewalPrice, { data: renewalPricing, isFetching: isPricingLoading }] = useLazyGetRenewalPriceQuery();

  const [refundPermanentNumber, { isLoading: isRefunding }] = useRefundPermanentNumberMutation();

  const handleRefund = async () => {
    if (!number) return;
    if (number.isCodeReceived) {
      toast.error(t('permanentNumbers.details.refundBlockedByCode'));
      return;
    }
    if (!window.confirm(t('permanentNumbers.details.refundConfirm'))) return;
    try {
      await refundPermanentNumber({ permanentNumberId: number._id }).unwrap();
      toast.success(t('permanentNumbers.details.refundSuccess'));
      refetch();
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message || t('permanentNumbers.details.refundError');
      toast.error(message);
    }
  };

  const durationOptions = [
    { key: 'oneDay', days: 1, labelKey: 'permanentNumbers.details.durationOneDay' },
    { key: 'threeDay', days: 3, labelKey: 'permanentNumbers.details.durationThreeDays' },
    { key: 'sevenDay', days: 7, labelKey: 'permanentNumbers.details.durationSevenDays' },
    { key: 'fourteenDay', days: 14, labelKey: 'permanentNumbers.details.durationFourteenDays' },
    { key: 'thirtyDay', days: 30, labelKey: 'permanentNumbers.details.durationThirtyDays' },
    { key: 'ninetyDay', days: 90, labelKey: 'permanentNumbers.details.durationNinetyDays' },
    { key: 'oneYear', days: 365, labelKey: 'permanentNumbers.details.durationOneYear' },
  ];

  const handleSelectDuration = (key: string) => {
    setSelectedDuration(key);
    if (number) {
      fetchRenewalPrice({ permanentNumberId: number._id, duration: key });
    }
  };

  const handleRenew = async () => {
    if (!number || !selectedDuration) return;
    const price = renewalPricing?.salePrice;
    const durationLabel = t(durationOptions.find((o) => o.key === selectedDuration)!.labelKey);
    if (!window.confirm(t('permanentNumbers.details.confirmRenewMessage', {
      duration: durationLabel,
      price: price ? `${price.toFixed(2)} USD` : '...',
    }))) return;
    try {
      await renewPermanentNumber({
        permanentNumberId: number._id,
        extensionDuration: selectedDuration,
      }).unwrap();
      toast.success(t('permanentNumbers.details.renewSuccess'));
      setShowRenewPicker(false);
      setSelectedDuration(null);
    } catch {
      toast.error(t('permanentNumbers.details.renewError'));
    }
  };

  const handleCopyNumber = async () => {
    if (!number) return;
    try {
      await navigator.clipboard.writeText(number.number);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError') || t('errors.network'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-5xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !number) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-5xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {error && typeof error === 'object' && 'data' in (error as object)
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <p className="text-sm text-neutral-medium font-body mb-1">
                  {t('permanentNumbers.details.number')}
                </p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-heading font-bold text-brand-accent">
                    {number.number}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyNumber}
                    className="px-3 py-2 bg-neutral-dark text-neutral-light rounded-lg border border-neutral-dark hover:border-brand-accent transition-colors duration-200 font-body text-sm"
                  >
                    {t('common.copy')}
                  </button>
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-body ${
                  number.status === 'active'
                    ? 'bg-green-500/20 text-green-500'
                    : number.status === 'paused'
                      ? 'bg-yellow-500/20 text-yellow-500'
                      : number.status === 'waiting'
                        ? 'bg-yellow-500/20 text-yellow-500'
                        : number.status === 'refunded'
                          ? 'bg-blue-500/20 text-blue-500'
                          : number.status === 'cancelled'
                            ? 'bg-red-500/20 text-red-500'
                            : 'bg-neutral-dark text-neutral-medium'
                }`}
              >
                {t(`permanentNumbers.status.${number.status}`)}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-neutral-medium font-body">
                  {t('permanentNumbers.details.service')}:{' '}
                  <span className="text-neutral-light font-heading">{number.serviceName}</span>
                </p>
                <p className="text-neutral-medium font-body">
                  {t('permanentNumbers.details.country')}:{' '}
                  <span className="text-neutral-light font-heading">{number.countryName}</span>
                </p>
                {(number.stateName || number.areaCode) && (
                  <p className="text-neutral-medium font-body">
                    {t('permanentNumbers.details.state')}:{' '}
                    <span className="text-neutral-light font-heading">{number.stateName || number.areaCode}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-neutral-medium font-body">
                  {t('permanentNumbers.details.duration')}:{' '}
                  <span className="text-neutral-light font-heading">
                    {t('permanentNumbers.purchase.durationDays', { days: number.ltrDuration })}
                  </span>
                </p>
                <p className="text-neutral-medium font-body">
                  {t('permanentNumbers.details.expiresAt')}:{' '}
                  <span className="text-neutral-light font-heading">
                    {formatDate(number.expiresAt, 'PP h:mm a', currentLanguage)}
                  </span>
                </p>
                <p className="text-neutral-medium font-body">
                  {t('permanentNumbers.details.price')}:{' '}
                  <span className="text-neutral-light font-heading">
                    {number.price.toFixed(2)} USD
                  </span>
                </p>
                {number.cashbackAmount ? (
                  <p className="text-neutral-medium font-body">
                    {t('permanentNumbers.details.cashback')}:{' '}
                    <span className="text-neutral-light font-heading">
                      {number.cashbackAmount.toFixed(2)} USD
                    </span>
                  </p>
                ) : null}


                {(canRefund || refundBlockedByCode) && (
                  <div className="pt-4">
                    <button
                      type="button"
                      onClick={handleRefund}
                      disabled={!canRefund || isRefunding}
                      className="px-6 py-3 bg-red-500/20 text-red-500 font-heading font-semibold rounded-lg hover:bg-red-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isRefunding ? (
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : null}
                      {t(refundBlockedByCode ? 'permanentNumbers.details.refundBlockedByCode' : 'permanentNumbers.details.refundNumber')}
                    </button>
                    <p className="text-xs text-neutral-medium mt-2">
                      {t(refundBlockedByCode ? 'permanentNumbers.details.refundBlockedDescription' : 'permanentNumbers.details.refundDescription')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {canRenew && (
            <div className="pt-4 border-t border-neutral-dark">
              {!showRenewPicker ? (
                <button
                  type="button"
                  onClick={() => setShowRenewPicker(true)}
                  className="px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('permanentNumbers.details.renewNumber')}
                </button>
              ) : (
                <div className="space-y-4">
                  <p className="text-neutral-medium font-body text-sm">
                    {t('permanentNumbers.details.selectDuration')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {durationOptions.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleSelectDuration(opt.key)}
                        className={`px-4 py-2 rounded-lg font-body text-sm transition-all duration-200 ${
                          selectedDuration === opt.key
                            ? 'bg-brand-accent text-brand-primary font-semibold'
                            : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                        }`}
                      >
                        {t(opt.labelKey)}
                      </button>
                    ))}
                  </div>
                  {selectedDuration && (
                    <div className="text-sm">
                      {isPricingLoading ? (
                        <span className="text-neutral-medium">{t('common.loading')}...</span>
                      ) : renewalPricing ? (
                        <span className="text-brand-accent font-heading font-semibold">
                          {renewalPricing.salePrice.toFixed(2)} USD
                        </span>
                      ) : null}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowRenewPicker(false);
                        setSelectedDuration(null);
                      }}
                      disabled={isRenewing}
                      className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRenew}
                      disabled={!selectedDuration || isRenewing || isPricingLoading}
                      className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isRenewing ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          <span>{t('common.loading')}</span>
                        </>
                      ) : (
                        t('permanentNumbers.details.confirmRenew', {
                          duration: selectedDuration
                            ? t(durationOptions.find((o) => o.key === selectedDuration)!.labelKey)
                            : '',
                        })
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          <SmsChecker
            temporaryNumberId={number._id}
            phoneNumber={number.number}
            isCodeReceived={isCodeReceived}
            receivedCodes={number.receivedCodes}
          />
        </div>
      </div>
    </div>
  );
}


